import LegalPageLayout from "@/components/LegalPageLayout";

/**
 * Public Privacy Policy page (/privacy).
 *
 * Required for:
 *  - Google OAuth verification (linked from consent screen)
 *  - Philippine Data Privacy Act (RA 10173) compliance
 *
 * Content is substantive and specific to MatchBroker's actual data
 * flows (broker account fields, PRC images, listing/requirement AI
 * parsing, PayMongo, Resend, LLM providers). Update the effective
 * date whenever data-handling practices change.
 */
export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      effectiveDate="September 4, 2026"
      testId="privacy-page"
    >
      <p>
        MatchBroker (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is a
        professional networking and matching platform for licensed Philippine
        real estate brokers. This Privacy Policy explains what personal
        information we collect, how we use it, when we share it, and the rights
        you have as a data subject under the{" "}
        <strong>Philippine Data Privacy Act of 2012 (RA 10173)</strong> and its
        implementing rules and regulations.
      </p>

      <h2>1. Who we are</h2>
      <p>
        MatchBroker is operated by <strong>Vancor Distribution, OPC</strong>, a
        one-person corporation registered in the Philippines. For any privacy
        matter, contact us at{" "}
        <a href="mailto:info@matchbroker.ph">info@matchbroker.ph</a>. Vancor
        Distribution, OPC is the Personal Information Controller (PIC) for
        MatchBroker&rsquo;s data processing activities.
      </p>

      <h2>2. Information we collect</h2>

      <h3>2.1 Information you provide when you register</h3>
      <ul>
        <li>
          <strong>Broker account details</strong>: full name, professional
          email address, mobile number (+63), and a password (which we store
          only as a one-way bcrypt hash — the plaintext is never persisted).
        </li>
        <li>
          <strong>PRC verification data</strong>: your Professional Regulation
          Commission (PRC) real estate broker license number and an image of
          your PRC ID card. We use this data solely to confirm that you are a
          licensed broker before granting access. PRC ID images are stored in
          object storage with restricted access.
        </li>
        <li>
          <strong>Referral code</strong> (optional) — links your registration
          to another broker&rsquo;s account so trial or Pro credits can be
          applied.
        </li>
      </ul>

      <h3>2.2 Information we receive when you sign in with Google</h3>
      <p>
        If you choose to sign in with Google, we receive only the following
        fields from your Google account:{" "}
        <strong>full name, email address, and profile picture URL</strong>. We{" "}
        <strong>do not</strong> access your Gmail, Contacts, Google Drive,
        Calendar, or any other Google service.
      </p>
      <p>
        MatchBroker&rsquo;s use and transfer of information received from
        Google APIs to any other app will adhere to the{" "}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
          data-testid="privacy-google-user-data-policy-link"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements.
      </p>

      <h3>2.3 Content you create on the platform</h3>
      <ul>
        <li>
          <strong>Property listings</strong>: raw pasted text plus AI-extracted
          structured fields (title, location, building/project, unit
          identifier, bedrooms, floor and lot area, parking, price,
          transaction type, category, exclusive-ATS flag, semantic features).
        </li>
        <li>
          <strong>Buyer / lessee requirements</strong>: raw pasted text plus
          AI-extracted structured fields (target location, budget, category,
          preferred features, notes).
        </li>
        <li>
          <strong>Match &amp; pipeline activity</strong>: which listings and
          requirements were matched, match scores, pipeline status changes
          (Suggested, Inquiry, Viewing, Offer, Closed, Archived), and any
          disputes you raise or receive on exclusive-ATS conflicts.
        </li>
        <li>
          <strong>Reports and moderation activity</strong>: if you flag a
          listing, we record who reported it, the reason, and any admin
          disposition.
        </li>
      </ul>

      <h3>2.4 Technical and usage data</h3>
      <ul>
        <li>
          Device information such as browser type, IP address (used for
          rate-limiting and lockout protection only), and timestamps of key
          events (login, listing/requirement create, password reset).
        </li>
        <li>
          Notification records — in-app, email, and (when configured) SMS
          messages sent to you, including subject/body summaries kept for
          delivery diagnostics.
        </li>
        <li>
          A JSON Web Token (JWT) issued at login and stored in your
          browser&rsquo;s local storage to keep you signed in.
        </li>
      </ul>

      <h2>3. How we use your information</h2>
      <ul>
        <li>
          <strong>Verification &amp; gatekeeping</strong>: to confirm you are a
          licensed PRC broker before granting network access.
        </li>
        <li>
          <strong>Matching engine</strong>: to compute semantic similarity
          scores between your listings and other brokers&rsquo; requirements
          (and vice versa), and to surface potential co-broker matches.
        </li>
        <li>
          <strong>Communications</strong>: password resets, verification
          approvals, new-match alerts, subscription and billing notifications,
          administrator broadcasts.
        </li>
        <li>
          <strong>Service operation &amp; security</strong>: authentication,
          brute-force protection (temporary lockouts), audit logs of
          admin/support actions, and platform performance monitoring.
        </li>
        <li>
          <strong>Legal compliance</strong>: to comply with obligations under
          Philippine law, respond to lawful requests from regulators, and
          enforce our Terms of Service.
        </li>
      </ul>

      <h2>4. Legal bases for processing (RA 10173)</h2>
      <ul>
        <li>
          <strong>Consent</strong>: you consent to our processing when you
          create an account and agree to this Privacy Policy.
        </li>
        <li>
          <strong>Contract necessity</strong>: to provide the platform services
          you subscribe to (verification, matching, pipeline management,
          billing).
        </li>
        <li>
          <strong>Legitimate interests</strong>: preventing fraud, enforcing
          exclusive-ATS integrity, ensuring platform security, and improving
          matching quality — balanced against your privacy interests.
        </li>
        <li>
          <strong>Legal obligation</strong>: retention of billing and audit
          records as required by tax, corporate, and consumer protection laws.
        </li>
      </ul>

      <h2>5. AI processing</h2>
      <p>
        Raw text you paste into the AI parse dialogs (listings and
        requirements) is transmitted to third-party large language model
        (LLM) providers for structured extraction and match-score
        computation. We send only the raw text and derived numeric embeddings
        — not your account credentials. LLM providers are contractually
        prohibited from training on our submissions. AI outputs are
        assistive and may contain errors; you remain responsible for
        reviewing extracted fields before saving.
      </p>

      <h2>6. Payments &amp; billing</h2>
      <p>
        Subscription payments are processed by <strong>PayMongo</strong>{" "}
        (Republic Act-registered Philippine payments processor). We never
        receive or store your card number, GCash credentials, or Maya
        credentials. We store only PayMongo transaction references,
        subscription tier, expiry date, and status metadata needed to grant
        or revoke access.
      </p>

      <h2>7. When and how we share your information</h2>
      <ul>
        <li>
          <strong>Other verified brokers on the network</strong>: your name
          and PRC-verified badge are visible in the co-broker directory.
          Once a match reaches the <em>Inquiry</em> stage in the shared
          pipeline, your listed contact channels (email and phone) become
          visible to the other broker so the deal can progress off-platform.
        </li>
        <li>
          <strong>Service providers (processors on our behalf)</strong>:
          PayMongo (payments), Resend (transactional email delivery), LLM
          providers (text extraction and matching), cloud hosting and object
          storage providers.
        </li>
        <li>
          <strong>Regulators and law enforcement</strong>: when we are
          required by valid legal process or Philippine law.
        </li>
        <li>
          We <strong>do not sell</strong> your personal data to third parties
          for advertising or profiling. We do not share your PRC ID image
          with anyone outside our verification team and the affected data
          subject.
        </li>
      </ul>

      <h2>8. Retention</h2>
      <p>
        We retain your account and content data for as long as your account
        is active, and for a reasonable period thereafter to comply with
        legal, tax, and audit obligations (typically up to five years from
        your last transaction). When you delete your account, we remove or
        anonymize personal identifiers except where retention is required
        by law.
      </p>

      <h2>9. Security measures</h2>
      <ul>
        <li>
          Passwords are stored as bcrypt hashes; the plaintext is never
          persisted.
        </li>
        <li>All traffic between your browser and our servers is encrypted with TLS.</li>
        <li>
          Role-based access control: administrator sub-roles (super_admin,
          verifier, support) grant only the permissions required for each
          function, with every privileged action written to an append-only
          audit log.
        </li>
        <li>
          Password reset tokens are single-use, SHA-256-hashed at rest, and
          expire in 30 minutes; rate limits and lockouts protect against
          brute-force attempts.
        </li>
        <li>
          Impersonation tokens issued by support agents are strictly scoped:
          they cannot access admin routes or make payments, and every use
          is audit-logged.
        </li>
      </ul>

      <h2>10. Your rights as a data subject</h2>
      <p>
        Under RA 10173, you have the right to be informed and to:
      </p>
      <ul>
        <li>
          <strong>Access</strong> the personal data we hold about you.
        </li>
        <li>
          <strong>Object</strong> to certain processing activities.
        </li>
        <li>
          <strong>Correct</strong> inaccurate data (available in your profile
          settings; contact us for restricted fields such as PRC data).
        </li>
        <li>
          <strong>Erase or block</strong> personal data that is no longer
          necessary or was collected unlawfully.
        </li>
        <li>
          <strong>Damages</strong>: seek indemnity for damages sustained due
          to inaccurate, incomplete, outdated, false, unlawfully obtained, or
          unauthorized use of personal data.
        </li>
        <li>
          <strong>Data portability</strong>: obtain an electronic copy of
          your data in a commonly used, machine-readable format.
        </li>
        <li>
          <strong>File a complaint</strong> with the{" "}
          <a
            href="https://www.privacy.gov.ph/complaints-main/"
            target="_blank"
            rel="noreferrer"
          >
            National Privacy Commission (NPC)
          </a>{" "}
          if you believe your rights have been violated.
        </li>
      </ul>
      <p>
        To exercise these rights, email us at{" "}
        <a href="mailto:info@matchbroker.ph">info@matchbroker.ph</a> from the
        email address associated with your MatchBroker account. We will
        respond within a reasonable time as required by law.
      </p>

      <h2>11. Cookies and local storage</h2>
      <p>
        We use your browser&rsquo;s local storage to hold a JWT session token
        that keeps you signed in, plus small preference flags (e.g. the
        parent-session cache when a support agent is impersonating a broker).
        We do not use tracking cookies for advertising.
      </p>

      <h2>12. Children</h2>
      <p>
        MatchBroker is a professional platform. It is not directed at, and
        does not knowingly collect data from, individuals under 18 years of
        age. Every user must be a licensed PRC real estate broker, which is
        a professional credential available only to adults.
      </p>

      <h2>13. International transfers</h2>
      <p>
        Some of our service providers (LLM providers, transactional email,
        cloud hosting) process data outside the Philippines. When personal
        data is transferred abroad, we use providers with contractual
        safeguards consistent with RA 10173&rsquo;s cross-border transfer
        requirements.
      </p>

      <h2>14. Updates to this Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material
        changes will be signalled via an in-app banner or email to your
        registered address. Continued use of MatchBroker after an update
        constitutes acceptance of the revised policy.
      </p>
    </LegalPageLayout>
  );
}
